import { useNavigate } from "@tanstack/react-router";
import { isNumber } from "lodash-es";
import { useCallback } from "react";

import { ScrollableTabsList } from "@/components/Tabs";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsTrigger } from "@/components/ui/tabs";
import { defaultNS, useTranslation } from "@/lib/i18n";
import { typedKeys } from "@/utils/object-utils";

import { EducationAndQualificationType } from "../-zod-schema";
import { useCertificationsQuery } from "../certification/-query";

type Props = {
  personId: string;
  educationAndQualificationType?: EducationAndQualificationType;
};

export function EducationAndQualificationTabs({
  personId,
  educationAndQualificationType,
}: Props) {
  const navigate = useNavigate({
    from: "/person/$id/education-and-qualification",
  });

  const handleTabChange = useCallback(
    (tab: EducationAndQualificationType) => {
      navigate({
        search: (prev) => ({
          ...prev,
          educationAndQualificationType: tab,
          pageIndex: 0, // 検索時はページを先頭に戻す
        }),
      });
    },
    [navigate],
  );

  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.person.profile.educationsAndQualifications",
  });

  const { data: certifications } = useCertificationsQuery({
    path: {
      personId: personId ?? "",
    },
    query: {
      freeText: null,
    },
  });

  const tabToQuantity = {
    [EducationAndQualificationType.CERTIFICATION]: certifications?.count,
    [EducationAndQualificationType.ARCHIVED]: undefined,
  };

  const tabToLabel = {
    [EducationAndQualificationType.CERTIFICATION]: t("tabs.certification"),
    [EducationAndQualificationType.ARCHIVED]: t("tabs.archived"),
  };

  return (
    <Tabs
      value={educationAndQualificationType}
      onValueChange={handleTabChange}
      className="flex-1 min-w-0"
    >
      <ScrollableTabsList className="flex gap-4 p-0">
        {typedKeys(EducationAndQualificationType)
          .filter((tab) => tab !== EducationAndQualificationType.ARCHIVED)
          .map((tab) => (
            <TabsTrigger
              key={tab}
              value={tab}
              className="h-8 gap-2 data-[state=active]:shadow-none"
            >
              {tabToLabel[tab]}
              {isNumber(tabToQuantity[tab]) && (
                <span className="ml-1 text-xs animate-in fade-in">
                  {tabToQuantity[tab]}
                </span>
              )}
            </TabsTrigger>
          ))}
        <Separator orientation="vertical" className="h-8" />
        <TabsTrigger
          key={EducationAndQualificationType.ARCHIVED}
          value={EducationAndQualificationType.ARCHIVED}
          className="h-8 gap-2 data-[state=active]:shadow-none"
        >
          {tabToLabel[EducationAndQualificationType.ARCHIVED]}
        </TabsTrigger>
      </ScrollableTabsList>
    </Tabs>
  );
}
